import { Box, Button, Container, HStack } from '@chakra-ui/react';
import { EventType } from '@components/Event';
import { useLivestreamData } from '@components/Event/provider/LivestreamDataProvider';
import SponsorSlideshow from '@components/Sponsor/SponsorSlideshow';
import { useAnalytics } from '@lib/analytics';
import { isBreakProgram } from '@lib/data-transform/event-schedules';
import { useFirebaseApp } from '@lib/firebase';
import { ExhibitorType } from '@lib/types';
import NextLink from 'next/link';
import { FunctionComponent, useState } from 'react';
import { useCollectionDataOnce, useDocumentDataOnce } from 'react-firebase-hooks/firestore';
import { FaChevronRight, FaExternalLinkAlt } from 'react-icons/fa';
import { FormattedMessage, useIntl } from 'react-intl';

const LiveExpoAds: FunctionComponent<{ event: EventType }> = ({ event }) => {
    const { app } = useFirebaseApp();
    const { currentProgram } = useLivestreamData();
    const [close, onClose] = useState(false);
    const [siteConfig] = useDocumentDataOnce(
        app.firestore().doc(`/events/${event.id}/site-config/expo`)
    );
    const ref = app.firestore().collection(`/events/${event.id}/expo`).orderBy('priority', 'asc');
    const [sponsors] = useCollectionDataOnce<ExhibitorType>(siteConfig ? ref : null, {
        idField: 'id'
    });

    const intl = useIntl();
    const { analytics } = useAnalytics();

    const showAdsEnabled = siteConfig && siteConfig.show_expo_ads;

    //ads are shown if current program is a break and if sponsors exist
    const showAds =
        showAdsEnabled &&
        currentProgram &&
        isBreakProgram(currentProgram) &&
        sponsors &&
        sponsors.length;

    return showAds ? (
        <Box py="4" px="2" mx="auto" bg="gray.100" boxShadow="inset 0px 5px 10px rgba(0,0,0,0.5)">
            {!close ? (
                <>
                    <Container px="0" maxW="80ch">
                        <HStack w="full" justify="space-between" color="gray.500">
                            <Box fontSize="sm" pl="2" textTransform="uppercase" fontWeight="bold">
                                <FormattedMessage
                                    id="player.exhibition_ads.title"
                                    defaultMessage="Virtuelle Ausstellung"
                                />
                            </Box>
                            <Box>
                                <NextLink
                                    passHref
                                    href={
                                        event.ext_expo_url
                                            ? event.ext_expo_url
                                            : `/events/${event.id}/exhibition`
                                    }>
                                    <Button
                                        variant="ghost"
                                        colorScheme="gray"
                                        size="sm"
                                        as="a"
                                        rightIcon={
                                            event.ext_expo_url ? (
                                                <FaExternalLinkAlt />
                                            ) : (
                                                <FaChevronRight />
                                            )
                                        }
                                        target={event.ext_expo_url ? '_blank' : '_self'}
                                        onClick={() => {
                                            analytics.send(
                                                'exhibition_ad_view_all_exhibitors_click',
                                                {
                                                    _action: 'click',
                                                    _category: 'exhibition_ad',
                                                    event_id: event.id
                                                }
                                            );
                                        }}>
                                        <FormattedMessage
                                            id="player.exhibition_ads.view_all_exhibitors"
                                            defaultMessage="Alle Aussteller"
                                        />
                                    </Button>
                                </NextLink>
                            </Box>
                        </HStack>
                    </Container>
                    <Container
                        maxW="80ch"
                        minH="140px"
                        p="2"
                        boxShadow="lg"
                        bg="white"
                        borderRadius="lg"
                        position="relative"
                        overflow="hidden">
                        <SponsorSlideshow
                            sponsors={
                                sponsors?.filter((s) => s.live_expo_ads_disabled !== true) || []
                            }
                            event={event}
                        />
                    </Container>
                </>
            ) : null}

            <Box textAlign="center">
                <Button variant="link" size="xs" onClick={() => onClose(!close)}>
                    {close
                        ? intl.formatMessage({
                              id: 'player.exhibition_ads.show_exhibitor',
                              defaultMessage: 'Aussteller einblenden'
                          })
                        : intl.formatMessage({
                              id: 'player.exhibition_ads.hide_exhibitor',
                              defaultMessage: 'Ausblenden'
                          })}
                </Button>
            </Box>
        </Box>
    ) : null;
};

export default LiveExpoAds;
